import {
  Popover,
  PopoverButton,
  PopoverOverlay,
  PopoverPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import cn from "clsx";
import { Fragment } from "react";
import { IoClose } from "react-icons/io5";

type TextPopupInfoProps = {
  children?: React.ReactNode;
  title: string;
  content: string;
  linkUrl?: string;
  linkText?: string;
  placement?: "top" | "right" | "bottom" | "left";
};

export const TextPopupInfo: React.FC<TextPopupInfoProps> = ({
  children,
  title,
  content,
  linkUrl,
  linkText = "Learn more",
  placement = "right",
}) => {
  return (
    <Popover className="flex flex-col-reverse md:relative">
      <PopoverButton className="grey-80 text-xs underline outline-none md:mb-1">
        {children}
      </PopoverButton>

      <Transition>
        <TransitionChild
          enter="transition ease-out duration-200"
          leave="transition ease-in duration-150"
          enterFrom={cn("opacity-0", {
            "translate-y-1": placement === "top" || placement === "bottom",
            "translate-x-1": placement === "left",
            "-translate-x-1": placement === "right",
          })}
          enterTo="opacity-100 translate-x-0 translate-y-0"
          leaveFrom="opacity-100 translate-x-0 translate-y-0"
          leaveTo={cn("opacity-0", {
            "translate-y-1": placement === "top" || placement === "bottom",
            "translate-x-1": placement === "left",
            "-translate-x-1": placement === "right",
          })}
          as={Fragment}
        >
          <PopoverOverlay className="fixed inset-0 z-[49] bg-black/40 md:hidden" />
        </TransitionChild>

        <TransitionChild
          enter="transition ease-out duration-200"
          leave="transition ease-in duration-150"
          enterFrom={cn("opacity-0", {
            "translate-y-1": placement === "top" || placement === "bottom",
            "translate-x-1": placement === "left",
            "-translate-x-1": placement === "right",
          })}
          enterTo="opacity-100 translate-x-0 translate-y-0"
          leaveFrom="opacity-100 translate-x-0 translate-y-0"
          leaveTo={cn("opacity-0", {
            "translate-y-1": placement === "top" || placement === "bottom",
            "translate-x-1": placement === "left",
            "-translate-x-1": placement === "right",
          })}
          as={Fragment}
        >
          <div
            className={cn(
              "fixed left-0 top-0 z-[51] flex h-screen w-full items-center justify-center md:absolute md:z-10 md:size-auto",
              {
                "md:left-full md:top-1/2 md:ml-3 md:-translate-y-1/2": placement === "right",
                "md:left-0 md:top-1/2 md:-translate-x-full md:-translate-y-1/2 md:-ml-4":
                  placement === "left",
                "md:left-1/2 md:top-full md:mt-3 md:-translate-x-1/2": placement === "bottom",
                "md:left-1/2 md:bottom-full md:mb-3 md:-translate-x-1/2": placement === "top",
              },
            )}
          >
            <PopoverPanel static>
              {({ close }) => (
                <div className="relative">
                  <div className="text base border-grey-40 relative flex max-h-[80vh] w-[300px] flex-col rounded-lg border bg-white p-6 shadow-[6px_6px_8px_0px_rgba(0,0,0,0.08)] md:w-[280px]">
                    <p className="!mb-2 text-base font-semibold text-black">{title}</p>
                    <p className="text-sm whitespace-pre-line text-grey-90">{content}</p>

                    {linkUrl && linkText && (
                      <>
                        <span className="my-5 block h-px w-full bg-grey-40" />
                        <a
                          href={linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-grey-90 font-medium underline"
                        >
                          {linkText}
                        </a>
                      </>
                    )}

                    <button
                      onClick={() => close()}
                      aria-label="Close popover"
                      className={cn(
                        "hover:bg-grey-100 absolute z-10 inline-flex size-8 items-center justify-center rounded-full bg-white shadow-[0_4px_4px_0px_rgba(0,0,0,0.08)] focus:outline-none md:size-8",
                        "-right-3 -top-3 md:-right-4 md:-top-4 md:hidden",
                      )}
                    >
                      <IoClose className="text-2xl text-inherit" />
                    </button>
                  </div>
                  <div
                    className={cn(
                      "absolute hidden size-7 rotate-45 bg-white border-grey-40 md:block",
                      {
                        "left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 border-b border-l rounded-bl-xl":
                          placement === "right",
                        "right-0 top-1/2 translate-x-1/2 -translate-y-1/2 border-t border-r rounded-tr-xl":
                          placement === "left",
                        "left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 border-b border-r rounded-br-xl":
                          placement === "top",
                        "left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 border-t border-l rounded-tl-xl":
                          placement === "bottom",
                      },
                    )}
                  />
                </div>
              )}
            </PopoverPanel>
          </div>
        </TransitionChild>
      </Transition>
    </Popover>
  );
};
